import {
  AppVariation,
  type ClientConfig,
} from "src/client-config/getClientConfig.ts";
import { billRoute } from "src/admin/bill/bill.route.tsx";
import { notFoundRoute } from "src/admin/not-found/not-found.route.tsx";
import { dashboardRoute } from "src/admin/dashboard/dashboard.route.tsx";
import { routes } from "src/constants/routes.constants.ts";
import { loginPageRoute } from "src/auth/pages/LoginPage/LoginPage.route.tsx";
import { Navigate, redirect } from "react-router-dom";
import { loginPageB2BRoute } from "src/auth/pages/LoginPageB2B/LoginPageB2B.route.tsx";
import { DemoShop } from "src/auth/pages/LoginPageB2B/components/DemoShop.tsx";
import { Suspense } from "react";
import { getModules } from "src/system/getModules.ts";
import BasicConfigurationTab from "src/admin/administration/tabs/BasicConfigurationTab.tsx";
import { RouteErrorBoundary } from "src/components/ErrorBoundary/RouteErrorBoundary.tsx";
import SystemTab from "src/admin/administration/tabs/SystemTab/SystemTab.tsx";
import { DebugTab } from "src/admin/administration/tabs/DebugTab.tsx";
import { B2BGateEmptyScreen } from "src/admin/layout/B2BGateEmptyScreen/B2BGateEmptyScreen.tsx";
import { getModuleRouteObjects } from "src/system/getModuleRouteObjects.tsx";

const basicConfigurationRoute = {
  index: true,
  element: <BasicConfigurationTab />,
  ErrorBoundary: RouteErrorBoundary,
};

const systemTabRoute = {
  path: routes.admin.administration.system.root,
  element: <SystemTab />,
  ErrorBoundary: RouteErrorBoundary,
};

const debugRoute = {
  path: routes.admin.administration.debug.root,
  element: <DebugTab />,
  ErrorBoundary: RouteErrorBoundary,
};

const importProductsRoute = {
  lazy: () => import("src/admin/import/import.tsx"),
  path: routes.admin.import.root,
  children: [
    {
      index: true,
      lazy: () => import("src/admin/import/form/import-products.route.tsx"),
    },
  ],
};

export const buildRoutes = ({ config }: { config: ClientConfig }) => {
  if (config.appVariation === AppVariation.B2bGateDemo) {
    return getB2bGateDemoRoutes();
  }
  if (config.appVariation === AppVariation.B2bGate) {
    return getB2bGateRoutes(config);
  }
  if (config.appVariation === AppVariation.B2bGateSupplier) {
    return getB2bGateSupplierRoutes(config);
  }
  if (config.appVariation === AppVariation.Erp) {
    return getErpRoutes(config);
  }

  throw new Error(`Routes for [${config.appVariation}] are not defined`);
};

const getErpRoutes = (config: ClientConfig) => {
  const modules = getModules(config);
  const erpAdministration = {
    path: routes.admin.administration.root,
    lazy: () => import("src/admin/administration/administration.tsx"),
    children: [basicConfigurationRoute, systemTabRoute, debugRoute],
  };
  return [
    {
      path: routes.admin.root,
      lazy: () => import("src/admin/layout/admin.layout.tsx"),
      children: [
        dashboardRoute,
        erpAdministration,
        billRoute,
        importProductsRoute,
        ...modules.flatMap((module) =>
          getModuleRouteObjects({ module, clientConfig: config }),
        ),
        notFoundRoute,
      ],
    },
    loginPageRoute,
    {
      path: "*",
      loader: () => {
        return redirect(routes.admin.root);
      },
    },
  ];
};

const getB2bGateSupplierRoutes = (config: ClientConfig) => {
  const modules = getModules(config);
  const erpAdministration = {
    path: routes.admin.administration.root,
    lazy: () => import("src/admin/administration/administration.tsx"),
    children: [basicConfigurationRoute],
  };
  return [
    {
      path: routes.admin.root,
      lazy: () => import("src/admin/layout/admin.layout.tsx"),
      children: [
        {
          index: true,
          element: <B2BGateEmptyScreen />,
        },
        ...modules.flatMap((module) =>
          getModuleRouteObjects({ module, clientConfig: config }),
        ),
        erpAdministration,
        importProductsRoute,
        notFoundRoute,
      ],
    },
    loginPageB2BRoute,
    {
      path: "*",
      loader: () => {
        return redirect(routes.admin.root);
      },
    },
  ];
};

const getB2bGateRoutes = (config: ClientConfig) => {
  const modules = getModules(config);
  return [
    {
      path: routes.admin.root,
      lazy: () => import("src/admin/layout/admin.layout.tsx"),
      children: [
        billRoute,
        ...modules.flatMap((module) =>
          getModuleRouteObjects({ module, clientConfig: config }),
        ),
        notFoundRoute,
      ],
    },
    loginPageB2BRoute,
    {
      path: "*",
      loader: () => {
        return redirect(routes.admin.root);
      },
    },
  ];
};

const getB2bGateDemoRoutes = () => {
  return [
    {
      path: "/",
      element: (
        <Suspense fallback={null}>
          <DemoShop />
        </Suspense>
      ),
    },
    {
      path: "*",
      element: <Navigate to={"/"} />,
    },
  ];
};
